import React, { useEffect, useState, useCallback, Fragment, useRef } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import SuccessPopup from '../../../../Components/PopupNotifications/SuccessPopup'
import ErrorPopup from '../../../../Components/PopupNotifications/ErrorPopup'
import InfoPopup from '../../../../Components/PopupNotifications/InfoPopup'
import { getArticles, updateArticle, generateSectionContent, updateSection, updateSectionAnalyses, createSection, deleteSection, getSectionStatus, generateAllSections, getArticleStatus, deleteArticle } from '../../../../Requests/ArticleRequests'
import { getProject } from '../../../../Requests/ProjectRequests'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx'
import { saveAs } from 'file-saver'
import DOMPurify from 'dompurify'
import {
    PencilIcon,
    PlayIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    ClockIcon,
    DocumentIcon,
    PlusIcon,
    TrashIcon,
    Bars3Icon,
    CpuChipIcon,
    EyeIcon,
    ArrowLeftIcon,
    ChevronDownIcon
} from '@heroicons/react/24/outline'
import Loader from '../../../../Components/Loader'
import EditSectionModal from '../../../../Components/Articles/EditSectionModal'
import CreateArticleModal from '../../../../Components/Articles/CreateArticleModal'
import NoCreditsModal from '../../../../Components/Billing/NoCreditsModal'
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import {
    useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

// Helper function to convert decimal to fraction
function decimalToFraction(decimal) {
    if (!decimal || decimal % 1 === 0) return decimal.toString()

    const decimalPart = decimal % 1
    const wholePart = Math.floor(decimal)

    // Common fractions
    const fractions = {
        0.25: '¼',
        0.33: '⅓',
        0.5: '½',
        0.67: '⅔',
        0.75: '¾'
    }

    // Find closest fraction
    let closestFraction = null
    let minDiff = Infinity

    for (const [dec, frac] of Object.entries(fractions)) {
        const diff = Math.abs(decimalPart - parseFloat(dec))
        if (diff < minDiff) {
            minDiff = diff
            closestFraction = frac
        }
    }

    // Use fraction if close enough (within 0.05), otherwise show decimal
    if (minDiff < 0.05) {
        return wholePart > 0 ? `${wholePart}${closestFraction}` : closestFraction
    }

    return decimal.toString()
}

// Markdown Editor Component
function MarkdownEditor({ value, onChange, onSave, onCancel, isEditing }) {
    const { t } = useTranslation()
    const [showPreview, setShowPreview] = useState(false)

    if (!isEditing) {
        return (
            <div className="prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {value || t('articles.noContentGenerated')}
                </ReactMarkdown>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="flex items-center gap-1 px-2 py-1 text-xs border border-gray-300 rounded text-gray-600 hover:bg-gray-50"
                    >
                        <EyeIcon className="h-3 w-3" />
                        {showPreview ? t('common.edit') : t('common.preview')}
                    </button>
                    <span className="text-xs text-gray-500">
                        {t('articles.supportsMarkdown')}
                    </span>
                </div>
            </div>

            {showPreview ? (
                <div className="min-h-64 p-3 border border-gray-300 rounded-md bg-gray-50">
                    <div className="prose prose-sm max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {value || t('articles.noContentToPreview')}
                        </ReactMarkdown>
                    </div>
                </div>
            ) : (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="w-full h-64 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent resize-none text-sm font-mono"
                    placeholder={t('articles.editMarkdownPlaceholder')}
                />
            )}

            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                    {t('common.cancel')}
                </button>
                <button
                    onClick={onSave}
                    className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800"
                >
                    {t('common.saveChanges')}
                </button>
            </div>
        </div>
    )
}

// Simple Text Editor Component
function RichTextEditor({ value, onChange, onSave, onCancel, isEditing }) {
    const { t } = useTranslation()
    // Helper function to detect if content is HTML
    const isHTML = (str) => {
        return /<(p|div|h[1-6]|br|ul|ol|li|strong|em|b|i|span)[\s>]/i.test(str) || /<\/[a-z]+>/i.test(str)
    }

    if (!isEditing) {
        // If content is HTML, render it directly with proper styling
        if (isHTML(value)) {
            // Convert \n characters to <br> tags for proper line breaks
            const processedHTML = value.replace(/\n/g, '<br>')

            return (
                <div className="prose prose-sm max-w-none">
                    <div
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedHTML || t('articles.noContentGenerated')) }}
                        style={{
                            lineHeight: '1.6',
                            textAlign: 'justify'
                        }}
                    />
                </div>
            )
        }

        // If content is markdown/plain text, use ReactMarkdown
        return (
            <div className="prose prose-sm max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {value || t('articles.noContentGenerated')}
                </ReactMarkdown>
            </div>
        )
    }

    return (
        <div className="space-y-3">
            <textarea
                value={value || ''}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-64 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent resize-none text-sm"
                placeholder={t('articles.editContentPlaceholder')}
            />

            <div className="flex gap-2 justify-end">
                <button
                    onClick={onCancel}
                    className="px-3 py-1 text-sm border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                    {t('common.cancel')}
                </button>
                <button
                    onClick={onSave}
                    className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-gray-800"
                >
                    {t('common.saveChanges')}
                </button>
            </div>
        </div>
    )
}

// Sidebar Section Component for left panel (keeping for potential future use)
function SidebarSection({ section, index, project, onEdit, onGenerate, onDelete, onSelectForEdit, generatingSections, isSelected, onClick }) {
    const { t } = useTranslation()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: section.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    const getSectionStatusIcon = (section) => {
        const isCurrentlyGenerating = generatingSections.has(section.id)
        const hasContent = section.status === 'completed' || section.output
        const isGenerating = section.status === 'generating' || section.status === 'pending' || isCurrentlyGenerating
        const isFailed = section.status === 'failed'

        if (hasContent && !isCurrentlyGenerating) {
            return <CheckCircleIcon className="h-4 w-4 text-green-500" />
        } else if (isFailed && !isCurrentlyGenerating) {
            return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />
        } else if (isGenerating) {
            return <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
        }
        return <ClockIcon className="h-4 w-4 text-gray-400" />
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            onClick={onClick}
            className={`border rounded-lg p-3 cursor-pointer transition-colors bg-white ${isSelected ? 'border-black border-2 ' : ' hover:border-gray-300 hover:bg-gray-50'
                }`}
        >
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <button
                        {...attributes}
                        {...listeners}
                        className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0"
                        title={t('articles.dragToReorder')}
                    >
                        <Bars3Icon className="h-4 w-4" />
                    </button>
                    <span className="text-xs font-medium text-gray-500 flex-shrink-0">
                        {index + 1}.
                    </span>
                    <h4 className="text-sm font-medium truncate flex-1 min-w-0">
                        {section.section_title}
                    </h4>
                    {getSectionStatusIcon(section)}
                </div>

                <div className="flex gap-1 flex-shrink-0">
                    <button
                        onClick={(e) => { e.stopPropagation(); onEdit(section); }}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                        title={t('articles.editSection')}
                    >
                        <PencilIcon className="h-3 w-3" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(section.id); }}
                        className="p-1 text-red-500 hover:text-red-700 rounded hover:bg-red-50"
                        title={t('articles.deleteSection')}
                    >
                        <TrashIcon className="h-3 w-3" />
                    </button>
                </div>
            </div>
            <p className="text-xs text-gray-600 line-clamp-2">
                {section.section_instructions}
            </p>

            {/* Section Details */}
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                {section.recommended_pages && (
                    <span>{t('articles.pages')}: {decimalToFraction(Number(section.recommended_pages))}</span>
                )}
                {section.word_count && (
                    <span>{t('articles.words')}: {Number(section.word_count).toLocaleString()}</span>
                )}
            </div>

            {/* Generate/Regenerate Button */}
            <div className="mt-3">
                <button
                    onClick={(e) => { e.stopPropagation(); onGenerate(section.id); }}
                    disabled={generatingSections.has(section.id)}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-white text-black border text-sm rounded-md hover:bg-gray-800 disabled:opacity-50"
                >
                    {generatingSections.has(section.id) ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                        <>
                            <PlayIcon className="h-4 w-4" />
                            <span>{section.output ? t('articles.regenerate') : t('articles.generate')}</span>
                            {section.estimated_cost && (
                                <span className="ml-1 flex items-center gap-1 text-xs opacity-75">
                                    ({Number(section.estimated_cost).toFixed(2)}<CpuChipIcon className="h-3 w-3" />)
                                </span>
                            )}
                        </>
                    )}
                </button>
            </div>
        </div>
    )
}

// Original Sortable Section Component (keeping for compatibility if needed)
function SortableSection({ section, index, project, onEdit, onEditOutput, onGenerate, onDelete, generatingSection, generatingSections, editingOutput, handleSaveOutput, handleCancelEditOutput, setEditingOutput }) {
    const { t } = useTranslation()
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: section.id })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }

    const getSectionStatusIcon = (section) => {
        // Check if section is currently being generated (in our local state)
        const isCurrentlyGenerating = generatingSections.has(section.id)

        // Check if section has status field, otherwise fall back to output check
        const hasContent = section.status === 'completed' || section.output
        const isGenerating = section.status === 'generating' || section.status === 'pending' || isCurrentlyGenerating
        const isFailed = section.status === 'failed'

        if (hasContent && !isCurrentlyGenerating) {
            return <CheckCircleIcon className="h-5 w-5 text-green-500" />
        } else if (isFailed && !isCurrentlyGenerating) {
            return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
        } else if (isGenerating) {
            return <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
        }
        return <ClockIcon className="h-5 w-5 text-gray-400" />
    }

    const getSectionStatusText = (section) => {
        // Check if section is currently being generated (in our local state)
        const isCurrentlyGenerating = generatingSections.has(section.id)

        if (isCurrentlyGenerating) {
            return t('articles.generating')
        } else if (section.status === 'completed' || section.output) {
            return t('articles.generated')
        } else if (section.status === 'failed') {
            return t('articles.failed')
        } else if (section.status === 'generating') {
            return t('articles.generating')
        } else if (section.status === 'pending') {
            return t('articles.pending')
        }
        return t('articles.pending')
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="border rounded-lg p-4 bg-white"
        >
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                    <button
                        {...attributes}
                        {...listeners}
                        className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
                        title={t('articles.dragToReorder')}
                    >
                        <Bars3Icon className="h-5 w-5" />
                    </button>
                    <span className="text-sm font-medium text-gray-500">
                        {index + 1}.
                    </span>
                    <h4 className="text-base font-medium">
                        {section.section_title}
                    </h4>
                    {getSectionStatusIcon(section)}
                    <span className="text-sm text-gray-500">
                        {getSectionStatusText(section)}
                    </span>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={() => onEdit(section)}
                        className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                        title={t('articles.editSection')}
                    >
                        <PencilIcon className="h-4 w-4" />
                    </button>

                    <button
                        onClick={() => onEdit(section)}
                        className="p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100"
                        title={t('articles.manageFiles')}
                    >
                        <DocumentIcon className="h-4 w-4" />
                    </button>

                    <button
                        onClick={() => onDelete(section.id)}
                        className="p-2 text-red-500 hover:text-red-700 rounded-md hover:bg-red-50"
                        title={t('articles.deleteSection')}
                    >
                        <TrashIcon className="h-4 w-4" />
                    </button>

                    {/* Always show generate/regenerate button */}
                    {(section.status === 'completed' || section.output) ? (
                        <button
                            onClick={() => onGenerate(section.id)}
                            disabled={generatingSections.has(section.id)}
                            className="flex items-center gap-2 px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
                        >
                            {generatingSections.has(section.id) ? (
                                <Loader />
                            ) : (
                                <>
                                    <span>{t('articles.regenerate')}</span>
                                    {section.estimated_cost && (
                                        <span className="ml-1 flex items-center gap-1 text-xs opacity-75">
                                            ({Number(section.estimated_cost).toFixed(2)}<CpuChipIcon className="h-3 w-3" />)
                                        </span>
                                    )}
                                </>
                            )}
                        </button>
                    ) : (
                        <button
                            onClick={() => onGenerate(section.id)}
                            disabled={generatingSections.has(section.id)}
                            className="flex items-center gap-2 px-3 py-1 bg-black text-white text-sm rounded-md hover:bg-gray-800 disabled:opacity-50"
                        >
                            {generatingSections.has(section.id) ? (
                                <Loader />
                            ) : (
                                <>
                                    <PlayIcon className="h-4 w-4" />
                                    <span>
                                        {section.status === 'failed' ? t('articles.retry') : t('articles.generate')}
                                    </span>
                                    {section.estimated_cost && (
                                        <span className="ml-1 flex items-center gap-1 text-xs opacity-75">
                                            ({Number(section.estimated_cost).toFixed(2)}<CpuChipIcon className="h-3 w-3" />)
                                        </span>
                                    )}
                                </>
                            )}
                        </button>
                    )}
                </div>
            </div>

            <p className="text-sm text-gray-600 mb-3">
                {section.section_instructions}
            </p>

            {/* Section Details */}
            <div className="mb-3 flex items-center gap-4 text-xs text-gray-500">
                {section.recommended_pages && (
                    <span>{t('articles.expectedPages')}: {decimalToFraction(Number(section.recommended_pages))}</span>
                )}
                {section.pages && (
                    <span>{t('articles.actualPages')}: {decimalToFraction(Number(section.pages))}</span>
                )}
                {section.word_count && (
                    <span>{t('articles.words')}: {Number(section.word_count).toLocaleString()}</span>
                )}
            </div>

            {/* Analysis Files */}
            <div className="mb-3">
                <div className="text-xs text-gray-500 mb-1">
                    {t('articles.analysisFiles')}:
                </div>
                {section.files && section.files.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                        {section.files.slice(0, 3).map((fileUuid, index) => {
                            const analysis = project?.analysis?.find(a => a.uuid === fileUuid)
                            const displayName = analysis?.name || analysis?.meeting_name || `File ${index + 1}`
                            return (
                                <span
                                    key={fileUuid}
                                    className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded"
                                    title={displayName}
                                >
                                    {displayName.length > 15 ? `${displayName.slice(0, 15)}...` : displayName}
                                </span>
                            )
                        })}
                        {section.files.length > 3 && (
                            <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                +{section.files.length - 3} {t('articles.more')}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className="text-xs text-gray-400 italic">
                        {t('articles.noAnalysisFiles')}
                    </div>
                )}
            </div>

            {section.output && (
                <div className="bg-gray-50 rounded-md p-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600">{t('articles.generatedContent')}:</span>
                        <button
                            onClick={() => onEditOutput(section)}
                            className="p-1 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-200"
                            title={t('articles.editContent')}
                        >
                            <PencilIcon className="h-4 w-4" />
                        </button>
                    </div>

                    <RichTextEditor
                        value={editingOutput && editingOutput.id === section.id ? editingOutput.tempOutput : section.output}
                        onChange={(value) => setEditingOutput(prev => ({ ...prev, tempOutput: value }))}
                        onSave={() => handleSaveOutput(section.id, editingOutput.tempOutput)}
                        onCancel={handleCancelEditOutput}
                        isEditing={editingOutput && editingOutput.id === section.id}
                    />
                </div>
            )}
        </div>
    )
}

function ArticleDetail() {
    const { project_uuid, articleId } = useParams()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [article, setArticle] = useState(null)
    const [project, setProject] = useState(null)
    const [loading, setLoading] = useState(true)
    const [generatingSection, setGeneratingSection] = useState(null)
    const [generatingSections, setGeneratingSections] = useState(new Set())
    const [editingSection, setEditingSection] = useState(null)
    const [editingOutput, setEditingOutput] = useState(null)
    const [editingArticle, setEditingArticle] = useState(false)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [activePollingSections, setActivePollingSections] = useState(new Set())
    const activePollingSectionsRef = useRef(new Set())
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false)

    // Toast states
    const [successToast, setSuccessToast] = useState(null)
    const [errorToast, setErrorToast] = useState(null)
    const [infoToast, setInfoToast] = useState(null)
    const [insufficientCredits, setInsufficientCredits] = useState(null)

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    )


    // Function to check ALL section statuses on page load for accurate refresh detection
    const checkForOngoingGenerationsWithStatusCheck = useCallback(async (articleData) => {
        if (!articleData.sections) return

        const activeSections = []
        const sectionStatusPromises = []

        // Check EVERY section's individual status - don't filter anything
        articleData.sections.forEach(section => {

            // Create a promise to check this section's status
            const statusPromise = getSectionStatus(section.id)
                .then(statusRes => {
                    const { section: updatedSection, status, has_content } = statusRes.data

                    // Check multiple conditions to determine if section is actually generating
                    const hasProgress = statusRes.data.progress && statusRes.data.progress !== null
                    const isActivelyGenerating = (status === 'generating' || status === 'pending') && hasProgress


                    if (isActivelyGenerating) {
                        activeSections.push(section.id)

                        // Update the section in local state with current status
                        setArticle(prev => ({
                            ...prev,
                            sections: prev.sections.map(s =>
                                s.id === section.id ? { ...s, status: status } : s
                            )
                        }))
                    } else if (status === 'generating' || status === 'pending') {
                    } else {
                    }
                    return { sectionId: section.id, status, section: updatedSection, has_content }
                })
                .catch(error => {
                    console.error('Error checking section', section.id, ':', error)
                    return { sectionId: section.id, status: 'error', section: null }
                })

            sectionStatusPromises.push(statusPromise)
        })

        // Wait for all section status checks to complete
        try {
            await Promise.all(sectionStatusPromises)

            // Set generating sections and start polling
            if (activeSections.length > 0) {
                setGeneratingSections(new Set(activeSections))

                // For backward compatibility
                setGeneratingSection(activeSections[0])

                // Start polling for each active section
                activeSections.forEach(sectionId => {
                    pollSectionStatus(sectionId)
                })
            } else {
            }
        } catch (error) {
            console.error('Error during section status checks:', error)
            // Don't fallback to avoid duplicate polling
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (project_uuid && articleId) {
            // First get the project to get the project ID
            getProject(project_uuid).then((projectRes) => {
                setProject(projectRes.data)
                // Then get articles for that project
                return getArticles(projectRes.data.id)
            }).then((articlesRes) => {
                const foundArticle = articlesRes.data.articles.find(a => a.id === parseInt(articleId))
                if (foundArticle) {
                    setArticle(foundArticle)

                    // Check individual section statuses for current information
                    checkForOngoingGenerationsWithStatusCheck(foundArticle)
                } else {
                    navigate(`/project/${project_uuid}`)
                }
            }).catch((error) => {
                console.error('Error fetching article:', error)
                navigate(`/project/${project_uuid}`)
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [project_uuid, articleId, navigate, checkForOngoingGenerationsWithStatusCheck])

    const handleAddSection = async () => {
        try {
            const maxOrder = Math.max(...(article.sections?.map(s => s.order_index) || [0]))
            const sectionData = {
                article_id: parseInt(articleId),
                section_title: `${t('articles.newSection')} ${(article.sections?.length || 0) + 1}`,
                section_instructions: t('articles.pleaseProvideInstructions'),
                section_prompt: '',
                order_index: maxOrder + 1
            }

            const response = await createSection(sectionData)

            // Add the new section to the local state
            setArticle(prev => ({
                ...prev,
                sections: [...(prev.sections || []), response.data.section]
            }))
        } catch (error) {
            console.error('Error creating section:', error)
            setErrorToast(t('articles.failedToCreateSection'))
        }
    }

    const handleDeleteSection = async (sectionId) => {
        if (!window.confirm(t('articles.confirmDeleteSection'))) {
            return
        }

        try {
            await deleteSection(sectionId)

            // Remove the section from local state
            setArticle(prev => ({
                ...prev,
                sections: prev.sections.filter(section => section.id !== sectionId)
            }))
        } catch (error) {
            console.error('Error deleting section:', error)
            setErrorToast(t('articles.failedToDeleteSection'))
        }
    }

    const handleDragEnd = async (event) => {
        const { active, over } = event

        if (active.id !== over?.id) {
            const oldIndex = article.sections.findIndex(section => section.id === active.id)
            const newIndex = article.sections.findIndex(section => section.id === over.id)

            // Store original sections for comparison
            const originalSections = [...article.sections]
            const newSections = arrayMove(article.sections, oldIndex, newIndex)

            // Update order_index for all sections (starting from 1, not 0)
            const updatedSections = newSections.map((section, index) => ({
                ...section,
                order_index: index + 1
            }))

            // Update local state immediately for better UX
            setArticle(prev => ({
                ...prev,
                sections: updatedSections
            }))

            // Update order on the server - update all sections that changed position
            try {
                const updatePromises = updatedSections.map((section) => {
                    const originalSection = originalSections.find(s => s.id === section.id)
                    if (originalSection && originalSection.order_index !== section.order_index) {
                        return updateSection(section.id, { order_index: section.order_index })
                    }
                    return null
                }).filter(Boolean)

                if (updatePromises.length > 0) {
                    await Promise.all(updatePromises)
                }
            } catch (error) {
                console.error('Error updating section order:', error)
                setErrorToast(t('articles.failedToSaveSectionOrder'))
                // Restore original order on error
                setArticle(prev => ({
                    ...prev,
                    sections: originalSections
                }))
            }
        }
    }

    const handleGenerateSection = async (sectionId) => {
        // Prevent generating the same section multiple times
        if (generatingSections.has(sectionId)) {
            setInfoToast(t('articles.sectionAlreadyGenerating'))
            return
        }

        try {
            // Add to generating sections immediately
            setGeneratingSections(prev => new Set([...prev, sectionId]))

            // Set main generating section for legacy compatibility
            if (!generatingSection) {
                setGeneratingSection(sectionId)
            }

            const response = await generateSectionContent(sectionId)

            // For the new job system, we expect a 202 response
            if (response.status === 202) {
                // Start polling for this section
                pollSectionStatus(sectionId)
            } else {
                // Fallback for synchronous response
                setArticle(prev => ({
                    ...prev,
                    sections: prev.sections.map(section =>
                        section.id === sectionId ? response.data.section : section
                    )
                }))
                // Remove from generating sections
                setGeneratingSections(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(sectionId)
                    return newSet
                })

                // Clear main generating section if this was it
                if (generatingSection === sectionId) {
                    setGeneratingSection(null)
                }
            }
        } catch (error) {
            console.error('Error generating section content:', error)

            // Check if error is due to insufficient credits
            try {
                const errorResponse = JSON.parse(error.request?.response)
                if (errorResponse?.message === "Insufficient credits") {
                    setInsufficientCredits(
                        <NoCreditsModal
                            title={t('CreditsModal.Title')}
                            body={t('CreditsModal.Body')}
                            continue={t('CreditsModal.Continue')}
                            navLink="/pricing/credits"
                            handleClose={() => setInsufficientCredits(null)}
                        />
                    )
                } else {
                    setErrorToast(t('articles.failedToGenerateContent'))
                }
            } catch (parseError) {
                // If we can't parse the error response, show generic error
                setErrorToast(t('articles.failedToGenerateContent'))
            }

            // Remove from generating sections on error
            setGeneratingSections(prev => {
                const newSet = new Set(prev)
                newSet.delete(sectionId)
                return newSet
            })

            // Clear main generating section if this was it
            if (generatingSection === sectionId) {
                setGeneratingSection(null)
            }
        }
    }

    const handleEditSection = (section) => {
        setEditingSection(section)
    }

    const handleEditOutput = (section) => {
        setEditingOutput({
            ...section,
            tempOutput: section.output
        })
    }

    const handleSaveOutput = async (sectionId, newOutput) => {
        try {
            await updateSection(sectionId, { output: newOutput })

            // Update the section in the local state
            setArticle(prev => ({
                ...prev,
                sections: prev.sections.map(section =>
                    section.id === sectionId ? { ...section, output: newOutput } : section
                )
            }))

            setEditingOutput(null)
        } catch (error) {
            console.error('Error saving section output:', error)
            setErrorToast(t('articles.failedToSaveChanges'))
        }
    }

    const pollSectionStatus = async (sectionId) => {

        // Check if this section is already being polled to prevent duplicates (use ref for immediate check)
        if (activePollingSectionsRef.current.has(sectionId)) {
            return
        }

        // Mark this section as being polled (update both ref and state)
        activePollingSectionsRef.current.add(sectionId)
        setActivePollingSections(prev => new Set([...prev, sectionId]))

        try {
            const statusRes = await getSectionStatus(sectionId)
            const { section, status } = statusRes.data

            if (status === 'completed') {
                // Update the section in local state
                setArticle(prev => ({
                    ...prev,
                    sections: prev.sections.map(s =>
                        s.id === sectionId ? section : s
                    )
                }))

                // Remove from generating sections
                setGeneratingSections(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(sectionId)
                    return newSet
                })

                // Remove from active polling
                activePollingSectionsRef.current.delete(sectionId)
                setActivePollingSections(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(sectionId)
                    return newSet
                })

                // Clear main generating section if this was it
                if (generatingSection === sectionId) {
                    setGeneratingSection(null)
                }
            } else if (status === 'failed') {
                // Remove from generating sections
                setGeneratingSections(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(sectionId)
                    return newSet
                })

                // Remove from active polling
                activePollingSectionsRef.current.delete(sectionId)
                setActivePollingSections(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(sectionId)
                    return newSet
                })

                // Clear main generating section if this was it
                if (generatingSection === sectionId) {
                    setGeneratingSection(null)
                }

                setErrorToast(t('articles.sectionGenerationFailed'))
            } else if (status === 'pending' || status === 'generating') {
                // Add to generating sections if not already there
                setGeneratingSections(prev => new Set([...prev, sectionId]))

                // Continue polling after 15 seconds
                setTimeout(() => {
                    // Remove from active polling before next poll to allow it
                    activePollingSectionsRef.current.delete(sectionId)
                    setActivePollingSections(prev => {
                        const newSet = new Set(prev)
                        newSet.delete(sectionId)
                        return newSet
                    })
                    pollSectionStatus(sectionId)
                }, 15000)
            } else {
                // Remove from active polling for unknown status
                activePollingSectionsRef.current.delete(sectionId)
                setActivePollingSections(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(sectionId)
                    return newSet
                })
            }
        } catch (error) {
            console.error('Error checking section status:', error)

            // Remove from generating sections on error
            setGeneratingSections(prev => {
                const newSet = new Set(prev)
                newSet.delete(sectionId)
                return newSet
            })

            // Remove from active polling on error
            setActivePollingSections(prev => {
                const newSet = new Set(prev)
                newSet.delete(sectionId)
                return newSet
            })

            if (generatingSection === sectionId) {
                setGeneratingSection(null)
            }

            setErrorToast(t('articles.errorCheckingStatus'))
        }
    }

    const handleCancelEditOutput = () => {
        setEditingOutput(null)
    }

    const handleSaveSection = async (sectionData) => {
        try {
            // First update section basic info
            const sectionUpdateData = {
                section_title: sectionData.section_title,
                section_instructions: sectionData.section_instructions,
                section_prompt: sectionData.section_prompt,
                recommended_pages: sectionData.recommended_pages
            }

            const response = await updateSection(editingSection.id, sectionUpdateData)

            // Then update the analysis files if they changed
            if (sectionData.analysis_uuids) {
                await updateSectionAnalyses(editingSection.id, sectionData.analysis_uuids)
                // Update the files in the response
                response.data.section.files = sectionData.analysis_uuids
            }

            // Update the section in the local state
            setArticle(prev => ({
                ...prev,
                sections: prev.sections.map(section =>
                    section.id === editingSection.id ? response.data.section : section
                )
            }))

            setEditingSection(null)
        } catch (error) {
            console.error('Error updating section:', error)
            setErrorToast(t('articles.failedToUpdateSection'))
        }
    }

    const handleEditArticle = () => {
        setEditingArticle(true)
    }

    const handleGenerateAllSections = async () => {
        try {
            const response = await generateAllSections(articleId, true)
            const { generation_summary, detailed_results } = response.data


            // Add all sections that are generating or pending to generating state
            const activeSections = detailed_results
                ?.filter(result => result.status === 'generating' || result.status === 'pending')
                ?.map(result => result.section_id) || []


            if (activeSections.length > 0) {
                setGeneratingSections(prev => new Set([...prev, ...activeSections]))

                // Start polling for each active section
                activeSections.forEach(sectionId => {
                    pollSectionStatus(sectionId)
                })

                // Show success message with generation mode information
                const modeText = generation_summary.sequential_mode ? t('articles.sequential') : t('articles.parallel')
                setSuccessToast(t('articles.generationInitiated', { mode: modeText, count: activeSections.length }))
            } else {
                setInfoToast(t('articles.noSectionsQueued'))
            }
        } catch (error) {
            console.error('Error generating all sections:', error)

            // Check if error is due to insufficient credits
            try {
                const errorResponse = JSON.parse(error.request?.response)
                if (errorResponse?.message === "Insufficient credits") {
                    setInsufficientCredits(
                        <NoCreditsModal
                            title={t('CreditsModal.Title')}
                            body={t('CreditsModal.Body')}
                            continue={t('CreditsModal.Continue')}
                            navLink="/pricing/credits"
                            handleClose={() => setInsufficientCredits(null)}
                        />
                    )
                } else {
                    setErrorToast(t('articles.failedToGenerateSections'))
                }
            } catch (parseError) {
                // If we can't parse the error response, show generic error
                setErrorToast(t('articles.failedToGenerateSections'))
            }
        }
    }

    const handleArticleUpdate = (updatedArticle) => {
        setArticle(updatedArticle)
        setEditingArticle(false)
    }

    const handleViewFullArticle = () => {
        navigate(`/project/${project_uuid}/article/${articleId}/full`)
    }

    const handleExport = async (format) => {
        setExportDropdownOpen(false)

        try {
            if (format === 'pdf') {
                await exportToPDF()
            } else if (format === 'word') {
                await exportToWord()
            }
        } catch (error) {
            console.error('Export failed:', error)
            setErrorToast(t('articles.exportFailed'))
        }
    }

    const exportToPDF = async () => {
        // Create a new PDF document
        const pdfDoc = await PDFDocument.create()
        const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
        const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

        let page = pdfDoc.addPage()
        const { width, height } = page.getSize()
        const margin = 50
        let yPosition = height - margin

        // Title
        const titleFontSize = 20
        page.drawText(article.article_title, {
            x: margin,
            y: yPosition,
            size: titleFontSize,
            font: helveticaBoldFont,
            color: rgb(0, 0, 0),
        })
        yPosition -= titleFontSize + 10

        // Description
        if (article.article_details) {
            const descFontSize = 12
            page.drawText(article.article_details, {
                x: margin,
                y: yPosition,
                size: descFontSize,
                font: helveticaFont,
                color: rgb(0.4, 0.4, 0.4),
            })
            yPosition -= descFontSize + 20
        }

        // Sections
        const sections = article.sections?.sort((a, b) => a.order_index - b.order_index) || []

        for (let i = 0; i < sections.length; i++) {
            const section = sections[i]

            // Check if we need a new page
            if (yPosition < 100) {
                page = pdfDoc.addPage()
                yPosition = height - margin
            }

            // Section title
            const sectionTitleFontSize = 16
            const sectionTitle = `${i + 1}. ${section.section_title}`
            page.drawText(sectionTitle, {
                x: margin,
                y: yPosition,
                size: sectionTitleFontSize,
                font: helveticaBoldFont,
                color: rgb(0, 0, 0),
            })
            yPosition -= sectionTitleFontSize + 10

            // Section content
            if (section.output) {
                const contentFontSize = 11
                const lineHeight = contentFontSize + 2
                const maxWidth = width - 2 * margin

                // Function to process markdown tables for PDF
                const processTablesForPDF = (content) => {
                    // Split content into lines
                    const lines = content.split('\n')
                    const processedLines = []

                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i].trim()

                        // Check if this is a table row (starts and ends with |)
                        if (line.startsWith('|') && line.endsWith('|')) {
                            // Skip separator lines (|---|---|---|)
                            if (line.match(/^\|[\s-|]+\|$/)) {
                                continue
                            }

                            // Process table row
                            const cells = line.slice(1, -1).split('|').map(cell => cell.trim())
                            processedLines.push(cells.join('\t'))
                        } else {
                            processedLines.push(line)
                        }
                    }

                    return processedLines.join('\n')
                }

                // Clean the content and handle line breaks properly
                const cleanContent = processTablesForPDF(section.output)
                    .replace(/[#*`]/g, '') // Remove markdown
                    .replace(/\r\n/g, '\n') // Normalize line endings
                    .replace(/\r/g, '\n') // Handle Mac line endings

                // Split by double newlines to get paragraphs
                const paragraphs = cleanContent.split(/\n\s*\n/).filter(p => p.trim())

                for (const paragraph of paragraphs) {
                    // Split paragraph into lines by single newlines
                    const lines = paragraph.split(/\n/).filter(line => line.trim())

                    for (const line of lines) {
                        // Remove any remaining problematic characters
                        const cleanLine = line.trim().replace(/[\r\n\t]/g, ' ')

                        if (!cleanLine) continue

                        // Word wrapping for each line
                        const words = cleanLine.split(' ').filter(w => w.length > 0)
                        let currentLine = ''

                        for (const word of words) {
                            // Clean the word of any control characters
                            const cleanWord = word.replace(/[\x00-\x1F\x7F]/g, '')
                            const testLine = currentLine + (currentLine ? ' ' : '') + cleanWord

                            try {
                                const textWidth = helveticaFont.widthOfTextAtSize(testLine, contentFontSize)

                                if (textWidth <= maxWidth) {
                                    currentLine = testLine
                                } else {
                                    if (currentLine) {
                                        // Check if we need a new page
                                        if (yPosition < 50) {
                                            page = pdfDoc.addPage()
                                            yPosition = height - margin
                                        }

                                        page.drawText(currentLine, {
                                            x: margin,
                                            y: yPosition,
                                            size: contentFontSize,
                                            font: helveticaFont,
                                            color: rgb(0, 0, 0),
                                        })
                                        yPosition -= lineHeight
                                    }
                                    currentLine = cleanWord
                                }
                            } catch (error) {
                                console.warn('Error measuring text width, skipping word:', cleanWord)
                                // Skip problematic words and continue
                                continue
                            }
                        }

                        // Draw the last line
                        if (currentLine) {
                            try {
                                if (yPosition < 50) {
                                    page = pdfDoc.addPage()
                                    yPosition = height - margin
                                }

                                page.drawText(currentLine, {
                                    x: margin,
                                    y: yPosition,
                                    size: contentFontSize,
                                    font: helveticaFont,
                                    color: rgb(0, 0, 0),
                                })
                                yPosition -= lineHeight
                            } catch (error) {
                                console.warn('Error drawing text line:', currentLine)
                                // Continue processing even if one line fails
                            }
                        }
                    }

                    // Add extra space between paragraphs
                    yPosition -= lineHeight * 0.5
                }
            }

            yPosition -= 20 // Space between sections
        }

        // Generate PDF bytes
        const pdfBytes = await pdfDoc.save()

        // Download the PDF
        const blob = new Blob([pdfBytes], { type: 'application/pdf' })
        saveAs(blob, `${article.article_title.replace(/[^a-z0-9]/gi, '_')}.pdf`)

        setSuccessToast(t('articles.pdfExported'))
    }

    const exportToWord = async () => {
        // Create document sections array
        const docSections = []

        // Title
        docSections.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: article.article_title,
                        bold: true,
                        size: 32,
                    }),
                ],
                heading: HeadingLevel.TITLE,
            })
        )

        // Description
        if (article.article_details) {
            docSections.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: article.article_details,
                            italics: true,
                            size: 24,
                        }),
                    ],
                })
            )

            // Add spacing
            docSections.push(new Paragraph({ children: [new TextRun({ text: '' })] }))
        }

        // Sections
        const sections = article.sections?.sort((a, b) => a.order_index - b.order_index) || []

        sections.forEach((section, index) => {
            // Section title
            docSections.push(
                new Paragraph({
                    children: [
                        new TextRun({
                            text: `${index + 1}. ${section.section_title}`,
                            bold: true,
                            size: 28,
                        }),
                    ],
                    heading: HeadingLevel.HEADING_1,
                })
            )

            // Section content
            if (section.output) {
                // Function to process markdown tables for Word
                const processTablesForWord = (content) => {
                    // Split content into lines
                    const lines = content.split('\n')
                    const processedLines = []

                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i].trim()

                        // Check if this is a table row (starts and ends with |)
                        if (line.startsWith('|') && line.endsWith('|')) {
                            // Skip separator lines (|---|---|---|)
                            if (line.match(/^\|[\s-|]+\|$/)) {
                                continue
                            }

                            // Process table row with wider spacing for Word
                            const cells = line.slice(1, -1).split('|').map(cell => cell.trim())
                            processedLines.push(cells.join('    '))
                        } else {
                            processedLines.push(line)
                        }
                    }

                    return processedLines.join('\n')
                }

                // Simple markdown processing - remove markdown syntax
                const cleanContent = processTablesForWord(section.output)
                    .replace(/#{1,6}\s/g, '') // Remove headers
                    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
                    .replace(/\*(.*?)\*/g, '$1') // Remove italic
                    .replace(/`(.*?)`/g, '$1') // Remove code

                // Split into paragraphs
                const paragraphs = cleanContent.split('\n\n').filter(p => p.trim())

                paragraphs.forEach(paragraph => {
                    if (paragraph.trim()) {
                        docSections.push(
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: paragraph.trim(),
                                        size: 22,
                                    }),
                                ],
                            })
                        )
                    }
                })
            }

            // Add spacing between sections
            if (index < sections.length - 1) {
                docSections.push(new Paragraph({ children: [new TextRun({ text: '' })] }))
            }
        })

        // Create document
        const doc = new Document({
            sections: [
                {
                    properties: {},
                    children: docSections,
                },
            ],
        })

        // Generate and download
        const blob = await Packer.toBlob(doc)
        saveAs(blob, `${article.article_title.replace(/[^a-z0-9]/gi, '_')}.docx`)

        setSuccessToast(t('articles.wordExported'))
    }


    const handleDeleteArticle = () => {
        setShowDeleteConfirmation(true)
    }

    const confirmDeleteArticle = async () => {
        try {
            await deleteArticle(articleId)
            setSuccessToast(t('articles.articleDeletedSuccessfully'))
            navigate(`/project/${project_uuid}`)
        } catch (error) {
            console.error('Error deleting article:', error)
            setErrorToast(t('articles.failedToDeleteArticle'))
        } finally {
            setShowDeleteConfirmation(false)
        }
    }

    const cancelDeleteArticle = () => {
        setShowDeleteConfirmation(false)
    }


    if (loading) {
        return (
            <div className='w-full h-screen flex justify-center items-center'>
                <Loader />
            </div>
        )
    }

    if (!article) {
        return (
            <div className='w-full h-screen flex justify-center items-center'>
                <p>{t('articles.articleNotFound')}</p>
            </div>
        )
    }

    return (
        <div className='w-full h-screen'>
            {/* Full window layout with sidebar and main content */}
            <div className='flex h-full  '>
                {/* Left Sidebar - Sections List */}

                <div className='w-full max-w-md h-full flex flex-col bg-gray-100 shadow-xl p-4'>
                    {/* Header */}
                    <div className='flex bg-black text-white w-full p-4 text-center  flex-col rounded-lg py-4'>
                        <button className='w-fit text-xs flex gap-2 z-30'
                            type='button'
                            onClick={() => navigate(`/project/${project_uuid}`)}
                        >
                            <ArrowLeftIcon className='w-4 h-4'></ArrowLeftIcon>
                            {t('common.back')}
                        </button>
                        <div className='flex-grow -mt-4'>
                            {t('articles.articleSections')}
                        </div>
                    </div>

                    {/* Sections List */}
                    <div className="flex-1 overflow-y-scroll py-2 bg-gray-100">
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={article.sections?.map(s => s.id) || []}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="space-y-1">
                                    {article.sections?.sort((a, b) => a.order_index - b.order_index).map((section, index) => (
                                        <SidebarSection
                                            key={section.id}
                                            section={section}
                                            index={index}
                                            project={project}
                                            onEdit={handleEditSection}
                                            onGenerate={handleGenerateSection}
                                            onDelete={handleDeleteSection}
                                            onSelectForEdit={handleEditOutput}
                                            generatingSections={generatingSections}
                                            isSelected={false}
                                            onClick={() => { }}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>

                        {/* Add Section Button */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <button
                                onClick={handleAddSection}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 text-gray-600 text-sm rounded-md hover:bg-gray-50"
                            >
                                <PlusIcon className="h-4 w-4" />
                                {t('articles.addSection')}
                            </button>
                        </div>
                    </div>

                    {/* Generate All Button - Always Visible at Bottom */}
                    <div className="border-t border-gray-200 py-2">
                        <button
                            onClick={handleGenerateAllSections}
                            disabled={generatingSections.size > 0}
                            className="w-full flex items-center justify-center gap-2 px-3 py-3 bg-black text-white text-sm rounded-md hover:bg-gray-800 disabled:opacity-50"
                        >
                            <PlayIcon className="h-4 w-4" />
                            <span>{t('articles.generateAll')}</span>
                            {article.estimated_cost && (
                                <span className="ml-1 flex items-center gap-1 text-xs opacity-75">
                                    ({Number(article.estimated_cost).toFixed(2)}<CpuChipIcon className="h-3 w-3" />)
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                {/* Right Content Area - All Sections Content */}
                <div className='flex-1 h-full flex flex-col bg-gray-100 py-4 pr-4'>
                    {/* Article Header Card */}
                    <div className=' bg-white text-black w-full p-4 text-center rounded-lg shadow-xl flex justify-between items-center'>
                        <div className='text-left'>
                            <h2 className="text-xl font-semibold text-gray-900">{article.article_title}</h2>
                            <p className="text-sm text-gray-600 mt-1">{article.article_details}</p>
                        </div>

                        <div className='flex gap-2 items-center'>
                            <div className='relative'>
                                <button
                                    onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
                                    className='bg-black text-white text-sm rounded-md w-fit px-4 py-2 flex items-center gap-2 hover:bg-gray-800'
                                >
                                    {t('common.export')}
                                    <ChevronDownIcon className='w-4 h-4' />
                                </button>
                                {exportDropdownOpen && (
                                    <div className='absolute right-0 mt-2 bg-white border border-gray-200 rounded-md shadow-lg w-32 z-10'>
                                        <button
                                            onClick={() => handleExport('word')}
                                            className='block px-4 py-2 w-full text-left hover:bg-gray-100 text-gray-700'
                                        >
                                            {t('common.word')}
                                        </button>
                                        <button
                                            onClick={() => handleExport('pdf')}
                                            className='block px-4 py-2 w-full text-left hover:bg-gray-100 text-gray-700'
                                        >
                                            {t('common.pdf')}
                                        </button>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={handleEditArticle}
                                className="bg-black text-white text-sm rounded-md w-fit px-4 py-2 flex items-center gap-2 hover:bg-gray-800"
                            >
                                <PencilIcon className="h-4 w-4" />
                                {t('common.edit')}
                            </button>
                            <button
                                onClick={handleDeleteArticle}
                                className="bg-red-600 text-white text-sm rounded-md w-fit px-4 py-2 flex items-center gap-2 hover:bg-red-700"
                            >
                                <TrashIcon className="h-4 w-4" />
                                {t('common.delete')}
                            </button>
                        </div>
                    </div>

                    {/* Content Container */}
                    <div className='mt-4 bg-white rounded-lg shadow-xl flex-1 flex flex-col overflow-hidden'>
                        {/* All Sections Content */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {article.sections && article.sections.length > 0 ? (
                                <div className="space-y-8">
                                    {article.sections
                                        ?.sort((a, b) => a.order_index - b.order_index)
                                        .map((section, index) => (
                                            <div key={section.id} className="section-content">
                                                {/* Section Header */}
                                                <div className="flex items-center justify-between mb-4 pb-2 border-b border-gray-100">
                                                    <div className="flex items-center gap-3">
                                                        <span className="text-sm font-medium text-gray-500">
                                                            {index + 1}.
                                                        </span>
                                                        <h3 className="text-lg font-semibold text-gray-900">
                                                            {section.section_title}
                                                        </h3>
                                                        <div className="flex items-center gap-1">
                                                            {section.output ? (
                                                                <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                                            ) : generatingSections.has(section.id) ? (
                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                                                            ) : (
                                                                <ClockIcon className="h-4 w-4 text-gray-400" />
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {section.output && (
                                                            <button
                                                                onClick={() => handleEditOutput(section)}
                                                                className="flex items-center gap-1 px-2 py-1 border border-gray-300 text-gray-700 text-xs rounded hover:bg-gray-50"
                                                            >
                                                                <PencilIcon className="h-3 w-3" />
                                                                {t('common.edit')}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Section Content */}
                                                <div className="section-text">
                                                    {section.output ? (
                                                        <RichTextEditor
                                                            value={editingOutput && editingOutput.id === section.id ? editingOutput.tempOutput : section.output}
                                                            onChange={(value) => setEditingOutput(prev => ({ ...prev, tempOutput: value }))}
                                                            onSave={() => handleSaveOutput(section.id, editingOutput.tempOutput)}
                                                            onCancel={handleCancelEditOutput}
                                                            isEditing={editingOutput && editingOutput.id === section.id}
                                                        />
                                                    ) : (
                                                        <div className="text-center text-gray-500 py-8 border border-dashed border-gray-200 rounded-lg">
                                                            <DocumentIcon className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                                                            <p className="text-sm">{t('articles.noContentYet')}</p>
                                                            <p className="text-xs mt-1">{t('articles.clickGenerateHint')}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Section Divider */}
                                                {index < article.sections.length - 1 && (
                                                    <div className="mt-8 pt-4">
                                                        <div className="w-full border-t border-gray-200"></div>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    }
                                </div>
                            ) : (
                                <div className="text-center text-gray-500 py-12">
                                    <DocumentIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                                    <p className="text-lg">{t('articles.noSectionsYet')}</p>
                                    <p className="text-sm mt-2">{t('articles.addSectionsHint')}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Section Modal */}
            {editingSection && (
                <EditSectionModal
                    section={editingSection}
                    projectAnalyses={project?.analysis || []}
                    onSave={handleSaveSection}
                    onClose={() => setEditingSection(null)}
                />
            )}

            {/* Edit Article Modal */}
            {editingArticle && (
                <CreateArticleModal
                    projectId={project?.id}
                    article={article}
                    onSuccess={handleArticleUpdate}
                    onClose={() => setEditingArticle(false)}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Transition appear show={showDeleteConfirmation} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={cancelDeleteArticle}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full w-full md:max-w-2xl sm:p-6">
                                    <div>
                                        <div className="text-center">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-lg font-medium leading-6 text-gray-900 text-center"
                                            >
                                                {t('common.delete')} Article
                                            </Dialog.Title>
                                            <div className="mt-2">
                                                <p className="text-sm text-red-600 mt-2 text-center">
                                                    {t('articles.deleteConfirmationText')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                                        <button
                                            type="button"
                                            className="bg-red-700 text-white rounded-xl px-4 py-2 whitespace-nowrap text-sm w-full sm:col-start-2"
                                            onClick={confirmDeleteArticle}
                                        >
                                            {t('common.delete')} Permanently
                                        </button>
                                        <button
                                            type="button"
                                            className="border border-black px-4 py-2 rounded-xl text-sm w-full mt-3 sm:col-start-1 sm:mt-0"
                                            onClick={cancelDeleteArticle}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Toast Notifications */}
            {successToast && (
                <SuccessPopup
                    text={successToast}
                    handleClose={() => setSuccessToast(null)}
                />
            )}
            {errorToast && (
                <ErrorPopup
                    text={errorToast}
                    handleClose={() => setErrorToast(null)}
                />
            )}
            {infoToast && (
                <InfoPopup
                    text={infoToast}
                    handleClose={() => setInfoToast(null)}
                />
            )}

            {/* No Credits Modal */}
            {insufficientCredits}
        </div>
    )
}

export default ArticleDetail
